import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Avatar,
  Button,
  Divider,
  Collapse
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import TeamLogo from '../../../components/team/TeamLogo';

const TeamSummaryTable = ({ teams }) => {
  return (
    <Paper sx={{ mb: 4 }}>
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" gutterBottom>
          Team Summary
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {teams
            .sort((a, b) => {
              const avgA = a.players.length > 0 
                ? a.players.reduce((sum, p) => sum + p.attendancePercentage, 0) / a.players.length 
                : 0;
              const avgB = b.players.length > 0 
                ? b.players.reduce((sum, p) => sum + p.attendancePercentage, 0) / b.players.length 
                : 0;
              return avgB - avgA; // Sort by attendance (highest first)
            })
            .map((team) => {
              const averageAttendance = team.players.length > 0 
                ? team.players.reduce((sum, player) => sum + player.attendancePercentage, 0) / team.players.length
                : 0;
              
              const attendanceColor = averageAttendance >= 80 ? 'success' : 
                                    averageAttendance >= 60 ? 'warning' : 'error';
              
              return (
                <Card key={team.teamId} variant="outlined" sx={{ p: { xs: 1, sm: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    {/* Team Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}>
                      <TeamLogo 
                        team={{ logoUrl: team.logoUrl, name: team.teamName }}
                        size={40}
                        sx={{ mr: 2 }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {team.teamName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {team.managerName}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Stats */}
                    <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flex: { xs: '1 1 100%', sm: '0 0 auto' }, justifyContent: 'space-between' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {team.gamesPlayed}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Games
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {team.players.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Players
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center', minWidth: { xs: 80, sm: 100 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                          <Box sx={{ minWidth: { xs: 50, sm: 60 }, mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={averageAttendance}
                              color={attendanceColor}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            {averageAttendance.toFixed(1)}%
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Attendance
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={
                            averageAttendance >= 80 ? 'Good' : 
                            averageAttendance >= 60 ? 'Fair' : 'Poor'
                          } 
                          color={attendanceColor}
                          size="small"
                          onClick={() => {/* do nothing*/}}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              );
            })}
        </Box>
      </Box>
    </Paper>
  );
};

const WorstOffendersTable = ({ teams, isExpanded, onToggle }) => {
  // Helper to format dates
  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get all players from all teams and find those with lowest attendance
  const allPlayers = teams.flatMap(team => 
    team.players.map(player => ({
      ...player,
      teamName: team.teamName,
      teamLogo: team.logoUrl,
      teamGames: team.gamesPlayed
    }))
  );

  // Sort by attendance percentage (lowest first)
  const worstOffenders = allPlayers
    .filter(player => player.attendancePercentage < 80) // Only show players below 80%
    .sort((a, b) => a.attendancePercentage - b.attendancePercentage);

  if (worstOffenders.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ mb: 4 }}>
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
        onClick={() => onToggle()}
      >
        <WarningIcon sx={{ mr: 1, color: 'warning.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        <Typography variant="h6" sx={{ flex: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Players Needing Attention (Below 80% Attendance)
        </Typography>
        <Chip 
          label={`${worstOffenders.length} players`} 
          size="small" 
          color="warning"
          sx={{ mr: 2 }}
          onClick={() => {/* do nothing*/}}
        />
        <Box>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>
      
      <Collapse in={isExpanded}>
        <Divider />
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {worstOffenders.map((player) => {
              const attendanceColor = player.attendancePercentage >= 60 ? 'warning' : 'error';
              
              return (
                <Card key={`${player.userTenantId}-${player.teamName}`} variant="outlined" sx={{ p: { xs: 1, sm: 1.5 } }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {/* Player Info */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32, flexShrink: 0 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {player.playerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {player.position}
                        </Typography>
                        {player.teamJoinedDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Joined: {formatDate(player.teamJoinedDate)}
                          </Typography>
                        )}
                        {player.injuryPeriods && player.injuryPeriods.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {player.injuryPeriods.slice(0, 1).map((injury) => (
                              <Typography key={injury.id} variant="caption" color="error.main" display="block" sx={{ fontStyle: 'italic' }}>
                                🩹 {formatDate(injury.startUtc)} - {injury.endUtc ? formatDate(injury.endUtc) : 'Ongoing'}
                                {injury.notes && `: ${injury.notes}`}
                              </Typography>
                            ))}
                            {player.gamesMissedDueToInjury > 0 && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', ml: 2 }}>
                                Games missed due to injury: {player.gamesMissedDueToInjury}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Team */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 100%', sm: '0 1 auto' }, minWidth: { xs: 0, sm: 150 } }}>
                      <TeamLogo 
                        team={{ logoUrl: player.teamLogo, name: player.teamName }}
                        size={32}
                        sx={{ mr: 1, flexShrink: 0 }}
                      />
                      <Typography variant="body2" noWrap>
                        {player.teamName}
                      </Typography>
                    </Box>
                    
                    {/* Stats */}
                    <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flex: { xs: '1 1 100%', sm: '0 0 auto' }, justifyContent: 'space-between' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {player.gamesPlayed} / {player.gamesEligible + (player.gamesMissedDueToInjury || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Games
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center', minWidth: { xs: 80, sm: 100 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                          <Box sx={{ minWidth: { xs: 40, sm: 50 }, mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={player.attendancePercentage}
                              color={attendanceColor}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            {player.attendancePercentage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Attendance
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip 
                          label={player.attendancePercentage >= 60 ? 'Fair' : 'Poor'} 
                          color={attendanceColor}
                          size="small"
                          onClick={() => {/* do nothing*/}}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

const CondensedPlayerCard = ({ player }) => {
  const attendanceColor = player.attendancePercentage >= 80 ? 'success' : 
                         player.attendancePercentage >= 60 ? 'warning' : 'error';

  // Format injury period dates
  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5 }, 
      border: '1px solid', 
      borderColor: 'divider',
      borderRadius: 1,
      mb: 1,
      '&:hover': {
        bgcolor: 'action.hover'
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32 }}>
          <PersonIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {player.playerName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {player.position}
          </Typography>
          {player.teamJoinedDate && (
            <Typography variant="caption" color="text.secondary" display="block">
              Joined: {formatDate(player.teamJoinedDate)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { xs: 100, sm: 120 }, mr: { xs: 1, sm: 2 } }}>
          <Box sx={{ minWidth: { xs: 40, sm: 60 }, mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={player.attendancePercentage}
              color={attendanceColor}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            {player.attendancePercentage.toFixed(0)}%
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: { xs: 45, sm: 60 }, textAlign: 'right', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
          {player.gamesPlayed}/{player.gamesEligible + (player.gamesMissedDueToInjury || 0)}
        </Typography>
      </Box>
      
      {/* Injury Periods */}
      {player.injuryPeriods && player.injuryPeriods.length > 0 && (
        <Box sx={{ mt: 1, pl: { xs: 4, sm: 6 } }}>
          {player.injuryPeriods.map((injury) => (
            <Box key={injury.id} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="error.main" sx={{ fontStyle: 'italic' }}>
                🩹 Injured: {formatDate(injury.startUtc)} - {injury.endUtc ? formatDate(injury.endUtc) : 'Ongoing'}
                {injury.notes && ` - ${injury.notes}`}
              </Typography>
            </Box>
          ))}
          {player.gamesMissedDueToInjury > 0 && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Games missed due to injury: {player.gamesMissedDueToInjury}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const CondensedTeamSection = ({ team, isExpanded, onToggle }) => {
  const sortedPlayers = [...team.players].sort((a, b) => {
    if (b.attendancePercentage !== a.attendancePercentage) {
      return b.attendancePercentage - a.attendancePercentage;
    }
    return a.playerName.localeCompare(b.playerName);
  });

  const averageAttendance = team.players.length > 0 
    ? team.players.reduce((sum, player) => sum + player.attendancePercentage, 0) / team.players.length
    : 0;

  return (
    <Paper sx={{ mb: 2 }}>
      {/* Collapsed Header */}
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          display: 'flex', 
          alignItems: 'center', 
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
        onClick={() => onToggle()}
      >
        <TeamLogo 
          team={{ logoUrl: team.logoUrl, name: team.teamName }}
          size={40}
          sx={{ mr: { xs: 1, sm: 2 } }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} noWrap>
            {team.teamName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {team.managerName}
            </Typography>
            <Chip 
              label={`${team.players.length} players`} 
              size="small" 
              variant="outlined"
              onClick={() => {/* do nothing*/}}
            />
            <Chip 
              label={`${averageAttendance.toFixed(1)}% avg`} 
              size="small" 
              color={averageAttendance >= 80 ? 'success' : averageAttendance >= 60 ? 'warning' : 'error'}
              onClick={() => {/* do nothing*/}}
            />
          </Box>
        </Box>
        <Box>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      {/* Expanded Content */}
      <Collapse in={isExpanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {sortedPlayers.length > 0 ? (
            <Box>
              {sortedPlayers.map((player) => (
                <CondensedPlayerCard 
                  key={player.userTenantId}
                  player={player}
                />
              ))}
            </Box>
          ) : (
            <Alert severity="info">
              No players found for this team.
            </Alert>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const LeagueAttendanceReport = () => {
  const { leagueId } = useParams();
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [worstOffendersExpanded, setWorstOffendersExpanded] = useState(true);

  const fetchAttendanceData = useCallback(async () => {
    if (!leagueId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await unifiedApi.getLeagueAttendanceReport(leagueId);
      if (!data) {
        throw new Error('Failed to fetch attendance data');
      }

      setAttendanceData(data);
    } catch (err) {
      console.error('Failed to fetch attendance data:', err);
      setError(err.message || 'Failed to load attendance data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const toggleTeamExpansion = (teamId) => {
    setExpandedTeams(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          component={Link}
          to={`/admin/leagues/${leagueId}`}
          startIcon={<ArrowBackIcon />}
        >
          Back to League Dashboard
        </Button>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Button
          component={Link}
          to={`/admin/leagues/${leagueId}`}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
          size="small"
        >
          Back to League Dashboard
        </Button>
        
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Player Attendance Report
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {attendanceData?.leagueName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generated on {new Date(attendanceData?.generatedAt).toLocaleDateString()}
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {attendanceData?.teams?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Teams
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {attendanceData?.teams?.reduce((sum, team) => sum + team.players.length, 0) || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Players
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {attendanceData?.teams?.reduce((sum, team) => sum + team.gamesPlayed, 0) || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Games
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {attendanceData?.teams?.length > 0 ? (
                  attendanceData.teams
                    .flatMap(team => team.players)
                    .reduce((sum, player) => sum + player.attendancePercentage, 0) / 
                  attendanceData.teams.reduce((sum, team) => sum + team.players.length, 0)
                ).toFixed(1) : '0.0'}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                League Average
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Team Summary Table */}
      {attendanceData?.teams?.length > 0 && (
        <TeamSummaryTable teams={attendanceData.teams} />
      )}

      {/* Worst Offenders Table */}
      {attendanceData?.teams?.length > 0 && (
        <WorstOffendersTable 
          teams={attendanceData.teams} 
          isExpanded={worstOffendersExpanded}
          onToggle={() => setWorstOffendersExpanded(!worstOffendersExpanded)}
        />
      )}

      {/* Condensed Team Views */}
      {attendanceData?.teams?.length > 0 ? (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
            Team Details (Click to expand)
          </Typography>
          {attendanceData.teams
            .sort((a, b) => a.teamName.localeCompare(b.teamName))
            .map((team) => (
              <CondensedTeamSection 
                key={team.teamId} 
                team={team}
                isExpanded={expandedTeams.has(team.teamId)}
                onToggle={() => toggleTeamExpansion(team.teamId)}
              />
            ))}
        </Box>
      ) : (
        <Alert severity="info">
          No attendance data available for this league.
        </Alert>
      )}
    </AdminLayout>
  );
};

export default LeagueAttendanceReport;